import * as Dialog from '@radix-ui/react-dialog';
import type { ReactNode } from 'react';
import { Button } from './Button';
import { cn } from '@/lib/cn';

/**
 * ConfirmDialog — destructive-confirmation modal used by ReaderEdit's
 * "Remove reader" and (later) admin destructive actions. Built on
 * Radix Dialog rather than AlertDialog to avoid adding another dependency;
 * semantics are close enough given we autofocus the confirm button and
 * allow Esc to cancel.
 */
export interface ConfirmDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: ReactNode;
  description?: ReactNode;
  confirmLabel?: string;
  cancelLabel?: string;
  /** Visual variant for the confirm button. Defaults to `danger`. */
  confirmVariant?: 'primary' | 'danger';
  onConfirm: () => void | Promise<void>;
  /** When true, the confirm button shows a loading state + disables both actions. */
  isConfirming?: boolean;
  className?: string;
}

export function ConfirmDialog({
  open,
  onOpenChange,
  title,
  description,
  confirmLabel = 'Confirm',
  cancelLabel = 'Cancel',
  confirmVariant = 'danger',
  onConfirm,
  isConfirming = false,
  className,
}: ConfirmDialogProps) {
  const handleConfirm = async () => {
    await onConfirm();
  };

  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-40 bg-or-slate-900/55 backdrop-blur-sm data-[state=open]:animate-in data-[state=closed]:animate-out" />
        <Dialog.Content
          className={cn(
            'fixed left-1/2 top-1/2 z-50 w-[calc(100vw-32px)] max-w-md -translate-x-1/2 -translate-y-1/2 rounded-2xl border border-or-slate-100 bg-white p-6 shadow-or-pop focus:outline-none',
            className,
          )}
        >
          <Dialog.Title className="font-display text-[20px] font-bold text-or-slate-900">
            {title}
          </Dialog.Title>
          {description ? (
            <Dialog.Description className="mt-2 text-[13px] leading-relaxed text-or-slate-600">
              {description}
            </Dialog.Description>
          ) : null}
          <div className="mt-6 flex items-center justify-end gap-2">
            <Dialog.Close asChild>
              <Button variant="secondary" size="md" disabled={isConfirming}>
                {cancelLabel}
              </Button>
            </Dialog.Close>
            <Button
              variant={confirmVariant}
              size="md"
              onClick={handleConfirm}
              disabled={isConfirming}
              autoFocus
            >
              {isConfirming ? 'Working…' : confirmLabel}
            </Button>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
